import { useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Stack } from '@figma/fpl-components';
import { LabelSmRg, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { AppPaper, FlexCenVer } from '@/UI/styles';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { I18N_KEYS } from '@/language/keys';
import { Seedance25DraftBadge } from '@/components/Nodes/ModelComponents/Seedance25Enhance';
import GalleryParams from '../Gallery/GalleryParams';
import type { MediaAsset, TextAsset } from '@/types/api/assets';

const ImgVideoInfo = ({ selectedFile, nodeId }: { selectedFile: MediaAsset; nodeId?: string }) => {
  const info = [
    selectedFile.width && selectedFile.height ? `${selectedFile.width}x${selectedFile.height}` : undefined,
    selectedFile.duration !== undefined ? `${selectedFile.duration.toFixed(2)}s` : undefined,
    selectedFile.fps !== undefined ? `${selectedFile.fps.toFixed(2)} fps` : undefined,
  ]
    .filter(Boolean)
    .join(' | ');
  return (
    <FlexCenVer sx={{ gap: 1, mb: 2 }}>
      {nodeId ? <Seedance25DraftBadge nodeId={nodeId} asset={selectedFile} /> : null}
      {info && <LabelSmRg sx={{ color: token.textSecondary }}>{info}</LabelSmRg>}
    </FlexCenVer>
  );
};

interface ParamsBlockProps {
  nodeName?: string;
  presentation?: 'default' | 'embedded';
  selectedFile: TextAsset | MediaAsset;
  /** The node the file came from, for model-specific labels such as Seedance 2.5's Draft badge. */
  nodeId?: string;
}
export const ParamsPanel = ({ nodeName, selectedFile, nodeId, presentation = 'default' }: ParamsBlockProps) => {
  const { t } = useTranslation();
  const paramsInputs = useMemo(() => {
    return { ...(selectedFile?.params || {}), ...(selectedFile?.input || {}) };
  }, [selectedFile]);

  const content = (
    <>
      {(selectedFile?.type === 'image' || selectedFile?.type === 'video') && (
        <ImgVideoInfo selectedFile={selectedFile} nodeId={nodeId} />
      )}
      {nodeName && (
        <>
          <TextSmRg sx={{ mb: 1 }}>{t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.MODEL)}</TextSmRg>
          <EllipsisText
            data-testid="media-gallery-params-model-name"
            variant="body-sm-rg"
            sx={{ color: token.textSecondary, mb: 2, width: '100%' }}
            delay={500}
          >
            {nodeName}
          </EllipsisText>
        </>
      )}

      {(selectedFile?.params || selectedFile?.input) && <GalleryParams inputs={paramsInputs} container="gallery" />}
    </>
  );

  if (presentation === 'embedded') {
    return <Stack.Col {...stylex.props(styles.embedded)}>{content}</Stack.Col>;
  }

  return (
    <AppPaper
      onClick={(e) => e.stopPropagation()}
      width="240px"
      height="100%"
      sx={{ display: 'flex', flexDirection: 'column', p: 2, overflow: 'auto' }}
      className="wea-no-scrollbar"
    >
      {content}
    </AppPaper>
  );
};

const styles = stylex.create({
  embedded: {
    width: '100%',
    height: '100%',
    overflow: 'auto',
    backgroundColor: token.bg,
  },
});
